import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerUser, isValidUniversityEmail, getAllUniversities, engineeringFields, countriesOfOrigin } from '../utils/userAuth';
import './Auth.css';

function Register({ onLogin }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    studentId: '',
    phone: '',
    university: '',
    field: '',
    countryOfOrigin: '',
    graduationYear: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchUniversity, setSearchUniversity] = useState('');

  const allUniversities = getAllUniversities();

  // Filter universities based on search
  const filteredUniversities = searchUniversity
    ? allUniversities.filter(u =>
        u.name.toLowerCase().includes(searchUniversity.toLowerCase()) ||
        u.domain.toLowerCase().includes(searchUniversity.toLowerCase())
      )
    : allUniversities;

  // Generate graduation years (current year to +5 years)
  const currentYear = new Date().getFullYear();
  const graduationYears = Array.from({ length: 6 }, (_, i) => currentYear + i);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isValidUniversityEmail(formData.email)) {
      setError('Please use your university email address (.edu or .ca)');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!formData.university) {
      setError('Please select your university');
      return;
    }

    if (!formData.field) {
      setError('Please select your engineering field');
      return;
    }

    setLoading(true);

    try {
      const user = registerUser({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        studentId: formData.studentId,
        phone: formData.phone,
        university: formData.university,
        field: formData.field,
        countryOfOrigin: formData.countryOfOrigin,
        graduationYear: formData.graduationYear
      });

      // Create token for compatibility
      const token = 'local_' + user.id;
      onLogin(user, token);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-logo">
          <span className="peacock-icon-large">🎓</span>
          <h2 className="auth-title">Join ONTAP Global</h2>
          <p className="auth-subtitle">For Foreign Master's Graduate Engineers</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="verification-notice">
          <div className="notice-icon">🌍</div>
          <div className="notice-text">
            <strong>100+ Universities | USA & Canada</strong>
            <p>Connect with fellow international graduate engineers. Get verified within 7 days to unlock all features.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="John Doe"
            />
          </div>

          <div className="form-group">
            <label>University Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="yourname@university.edu"
            />
            <small className="field-hint">Must be .edu (USA) or .ca (Canada) email</small>
          </div>

          <div className="form-group">
            <label>University</label>
            <input
              type="text"
              placeholder="Search universities..."
              value={searchUniversity}
              onChange={(e) => setSearchUniversity(e.target.value)}
              className="search-input-field"
            />
            <select
              name="university"
              value={formData.university}
              onChange={handleChange}
              required
              className="community-select"
            >
              <option value="">Select your university...</option>
              <optgroup label="USA Universities">
                {filteredUniversities.filter(u => u.country === 'USA').map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.state})</option>
                ))}
              </optgroup>
              <optgroup label="Canada Universities">
                {filteredUniversities.filter(u => u.country === 'Canada').map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.province})</option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="form-group">
            <label>Engineering Field</label>
            <select
              name="field"
              value={formData.field}
              onChange={handleChange}
              required
              className="community-select"
            >
              <option value="">Select your field...</option>
              {engineeringFields.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Country of Origin</label>
            <select
              name="countryOfOrigin"
              value={formData.countryOfOrigin}
              onChange={handleChange}
              className="community-select"
            >
              <option value="">Select your country...</option>
              {countriesOfOrigin.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group half">
              <label>Graduation Year</label>
              <select
                name="graduationYear"
                value={formData.graduationYear}
                onChange={handleChange}
                className="community-select"
              >
                <option value="">Year...</option>
                {graduationYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="form-group half">
              <label>Student ID</label>
              <input
                type="text"
                name="studentId"
                value={formData.studentId}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (123) 456-7890"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Minimum 6 characters"
            />
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="Re-enter your password"
            />
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Login here</Link>
        </p>
      </div>
    </div>
  );
}

export default Register;
